<template>

<div class="asignados-page text-left w-full">
  <PageHeader title="Asignados" subtitle="Gestión de territorios asignados a publicadores" />

  <!-- Formulario para gestionar (crear) asignaciones -->
  <div class="asignados-content">
    <div class="asignar-card mb-4">
    <h3 class="mt-0 mb-3">Asignar territorio</h3>
    <div class="flex flex-column md:flex-row gap-2 align-items-stretch md:align-items-end">
      <div class="flex flex-column gap-1 flex-1">
        <label class="text-sm">Territorio</label>
        <Dropdown
          v-model="form.territorio"
          :options="terrTodos"
          placeholder="Elige un territorio"
          filter
          showClear
          class="w-full"
        />
      </div>
      <div class="flex flex-column gap-1 flex-1">
        <label class="text-sm">Asignar a</label>
        <InputText
          v-model="form.assignedTo"
          placeholder="Nombre del publicador"
          class="w-full"
          @keyup.enter="asignar"
        />
      </div>
      <Button label="Asignar" icon="pi pi-plus" :disabled="!form.territorio || !form.assignedTo || !!yaAsignado(form.territorio)" @click="asignar" />
    </div>
    <small v-if="form.territorio && yaAsignado(form.territorio)" class="text-orange-500">
      Ese territorio ya está asignado a {{ yaAsignado(form.territorio).assignedTo }}. Devuélvelo antes de reasignar.
    </small>
    </div><!-- /asignar-card -->

  <!-- Asignaciones vigentes -->
  <div class="asignar-card">
    <DataTable :value="activos" class="w-full" tableStyle="min-width: 100%">
      <Column field="territorio" header="Territorio"></Column>

      <Column field="assignedTo" header="Asignado a" v-if="!screen.isMobile"></Column>
      <Column  :header="screen.isMobile ? 'Asignado a' : 'Fecha de asignación'">
        <template #body="{data}">
          <div class="font-bold" v-if="screen.isMobile">
            {{ data.assignedTo }}
          </div>
          <div v-if="data.id">
            {{ fmt(data.id) }}
          </div>
        </template>
      </Column>
      <Column header="Tipo">
        <template #body="{data}">
          <div v-if="data.type === 'private'">
            Privado
          </div>
          <div v-else class="text-red-300">
            Público
          </div>
        </template>
      </Column>
      <Column header="">
        <template #body="{data}">
          <Button
            v-if="data.type === 'private'"
            icon="pi pi-check"
            label="Devolver"
            size="small"
            text
            @click="devolver(data)"
          />
          <Button v-else icon="pi pi-trash" text @click="unlink(data)" />
        </template>
      </Column>
    </DataTable>
  </div>

  <!-- Historial de asignaciones -->
  <div class="w-full px-2 mt-4">
    <h2>Historial de asignaciones</h2>

    <div class="grid">
      <!-- Por territorio -->
      <div class="col-12 md:col-6">
        <h3>Por territorio</h3>
        <Dropdown
          v-model="selTerr"
          :options="terrOptions"
          placeholder="Elige un territorio"
          showClear
          filter
          class="w-full mb-2"
        />
        <DataTable v-if="selTerr" :value="historialPorTerritorio" class="w-full" tableStyle="min-width: 100%">
          <Column field="assignedTo" header="Persona"></Column>
          <Column header="Desde">
            <template #body="{data}">{{ fmt(data.desde) }}</template>
          </Column>
          <Column header="Hasta">
            <template #body="{data}">
              <span v-if="data.hasta">{{ fmt(data.hasta) }}</span>
              <span v-else class="text-accent">Actual</span>
            </template>
          </Column>
        </DataTable>
        <p v-else class="text-sm text-500">Selecciona un territorio para ver entre qué fechas lo tuvo cada persona.</p>
      </div>

      <!-- Por persona -->
      <div class="col-12 md:col-6">
        <h3>Por persona</h3>
        <Dropdown
          v-model="selPersona"
          :options="personaOptions"
          placeholder="Elige una persona"
          showClear
          filter
          class="w-full mb-2"
        />
        <DataTable v-if="selPersona" :value="historialPorPersona" class="w-full" tableStyle="min-width: 100%">
          <Column field="territorio" header="Territorio"></Column>
          <Column header="Desde">
            <template #body="{data}">{{ fmt(data.desde) }}</template>
          </Column>
          <Column header="Hasta">
            <template #body="{data}">
              <span v-if="data.hasta">{{ fmt(data.hasta) }}</span>
              <span v-else class="text-accent">Actual</span>
            </template>
          </Column>
        </DataTable>
        <p v-else class="text-sm text-500">Selecciona una persona para ver qué territorios ha tenido.</p>
      </div>
    </div>
    </div><!-- /asignar-card historial -->
  </div><!-- /asignados-content -->
</div><!-- /asignados-page -->
</template>

<script setup>
import { ref, computed } from 'vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import { useTerritoriosStore } from "@/store/territorios";
import { useScreenStore } from '@/store/useScreenStore';
import { useStorage } from '@/composables/useStorage';

const screen = useScreenStore()
const territorios = useTerritoriosStore()
const { saveAssignments } = useStorage()

const fmt = (ts) => (ts ? new Date(Number(ts)).toLocaleDateString() : '')

// --- Formulario para crear asignaciones ---
const form = ref({ territorio: null, assignedTo: '' })

const terrTodos = computed(() =>
  territorios.listWithName.map((t) => t.territorio_nombre)
)

// Devuelve la asignación privada vigente de un territorio, si existe
const yaAsignado = (terr) =>
  territorios.asignadosActivos.find(
    (a) => a.type === 'private' && a.territorios.includes(terr)
  )

const asignar = async () => {
  if (!form.value.territorio || !form.value.assignedTo) return
  if (yaAsignado(form.value.territorio)) return
  territorios.$patch({
    asignados: [
      ...territorios.asignados,
      {
        id: Date.now(),
        territorios: [form.value.territorio],
        assignedTo: form.value.assignedTo.trim(),
        type: 'private',
        returnedAt: null,
      },
    ],
  })
  form.value = { territorio: null, assignedTo: '' }
  await saveAssignments()
}

// Asignación pública: se quita el territorio del documento (sin historial)
const unlink = async (data) => {
  territorios.$patch({
    asignados: territorios.asignados.map((item) => {
      if (item.id === data.id) {
        item.territorios = item.territorios.filter((t) => t !== data.territorio)
      }
      return item
    })
  })
  await saveAssignments()
}

// Asignación privada: se cierra el período (fecha de devolución) y se conserva como historial
const devolver = async (data) => {
  territorios.$patch({
    asignados: territorios.asignados.map((item) =>
      item.id === data.id ? { ...item, returnedAt: Date.now() } : item
    )
  })
  await saveAssignments()
}

// Filas planas de asignaciones vigentes (un territorio por fila)
const activos = computed(() => {
  let data = []
  for (const grupo of territorios.asignadosActivos) {
    for (const t of grupo.territorios) {
      data.push({ territorio: t, ...grupo })
    }
  }
  return data
})

// Historial completo de asignaciones privadas (vigentes + devueltas)
const historial = computed(() => {
  const rows = []
  for (const a of territorios.asignados) {
    if (a.type !== 'private') continue
    for (const t of a.territorios) {
      rows.push({
        id: a.id,
        territorio: t,
        assignedTo: a.assignedTo,
        desde: a.id,
        hasta: a.returnedAt || null,
      })
    }
  }
  return rows.sort((x, y) => Number(x.desde) - Number(y.desde))
})

const uniq = (arr) => [...new Set(arr)].sort()

const terrOptions = computed(() => uniq(historial.value.map((r) => r.territorio)))
const personaOptions = computed(() => uniq(historial.value.map((r) => r.assignedTo)))

const selTerr = ref(null)
const selPersona = ref(null)

const historialPorTerritorio = computed(() =>
  historial.value.filter((r) => r.territorio === selTerr.value)
)
const historialPorPersona = computed(() =>
  historial.value.filter((r) => r.assignedTo === selPersona.value)
)
</script>
<style scoped>
.asignados-page { width: 100%; }
.asignados-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: 0 var(--space-5) var(--space-5);
}
.asignar-card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  box-shadow: var(--shadow-1);
}
</style>
